import { type ReactNode, useRef, useState } from "react";

import { t } from "../../../i18n";
import { trpc } from "../../../lib/trpc";
import { useToast } from "../../ui";
import type { CategoryItem } from "../utils";
import { ChipsInput } from "./ChipsInput";

interface PatternChipRowProps {
  householdId: string;
  category: CategoryItem;
  /** Innhold som rendres pa samme linje som treff-teksten (eks. en bryter pa mobil). */
  trailing?: ReactNode;
}

/**
 * Inline pattern editor for a category row in the categories tab. Wraps
 * ChipsInput with the per-category update mutation, optimistic cache
 * write, and request coalescing so rapid edits don't fork seed
 * categories into duplicates.
 */
export function PatternChipRow({ householdId, category, trailing }: PatternChipRowProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [draft, setDraft] = useState("");
  // Patterns the user wanted while a previous mutation was still in flight.
  // Coalescing them into a single follow-up call avoids racing requests that
  // would each fork a seed category and produce duplicate household rows.
  const queuedRef = useRef<string[] | null>(null);
  const saveRef = useRef<(patterns: string[]) => void>(() => {});

  const writePatterns = (categoryId: string, patterns: string[]) =>
    utils.household.get.setData({ groupId: householdId }, (data) =>
      data
        ? {
            ...data,
            categories: data.categories.map((c) =>
              c.id === categoryId ? { ...c, matchPatterns: patterns } : c,
            ),
          }
        : data,
    );

  const update = trpc.household.updateCategory.useMutation({
    onMutate: async (input) => {
      await utils.household.get.cancel({ groupId: householdId });
      const prev = utils.household.get.getData({ groupId: householdId });
      writePatterns(input.categoryId, input.category.matchPatterns);
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      queuedRef.current = null;
      if (ctx?.prev) utils.household.get.setData({ groupId: householdId }, ctx.prev);
      show({
        tone: "danger",
        title: t.household.categories.updateFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
    onSuccess: (result, input) => {
      // Seed categories (group_id IS NULL) get forked into a household-scoped
      // row on first edit — the returned id differs from the seed id. Swap the
      // cached id so the next edit targets the fork instead of re-forking.
      // (Mock mode returns { ok } and is not affected.)
      if (!result || !("id" in result) || result.id === input.categoryId) return;
      const forkedId = result.id;
      utils.household.get.setData({ groupId: householdId }, (data) =>
        data
          ? {
              ...data,
              categories: data.categories.map((c) =>
                c.id === input.categoryId ? { ...c, id: forkedId } : c,
              ),
            }
          : data,
      );
    },
    onSettled: () => {
      const next = queuedRef.current;
      queuedRef.current = null;
      if (next !== null) {
        saveRef.current(next);
        return;
      }
      // Reconcile against the server in the background — matches the optimistic
      // pattern used in the expense table. The next mutate's onMutate will
      // cancel this refetch if the user keeps editing.
      utils.household.get.invalidate({ groupId: householdId });
    },
  });

  const save = (patterns: string[]) => {
    writePatterns(category.id, patterns);
    if (update.isPending) {
      queuedRef.current = patterns;
      return;
    }
    update.mutate({
      groupId: householdId,
      categoryId: category.id,
      category: {
        name: category.name,
        icon: category.icon,
        color: category.color,
        defaultShared: category.defaultShared,
        matchPatterns: patterns,
      },
    });
  };
  saveRef.current = save;

  const add = () => {
    const next = draft.trim();
    if (!next) return;
    setDraft("");
    const exists = category.matchPatterns.some((p) => p.toUpperCase() === next.toUpperCase());
    if (exists) return;
    save([...category.matchPatterns, next]);
  };

  const isWorking = update.isPending || queuedRef.current !== null;

  return (
    <div className="space-y-1.5">
      <ChipsInput
        chips={category.matchPatterns}
        draft={draft}
        onDraftChange={setDraft}
        onAdd={add}
        onRemove={(p) => save(category.matchPatterns.filter((x) => x !== p))}
        onRemoveLast={() => save(category.matchPatterns.slice(0, -1))}
        placeholder={
          category.matchPatterns.length === 0
            ? t.household.categories.firstPatternPlaceholder
            : t.household.categories.addPatternPlaceholder
        }
        addButtonLabel={t.household.categories.addPatternButton}
        removeLabel={t.household.categories.removePatternLabel}
        loading={isWorking}
      />
      {category.transactionCount !== undefined || trailing ? (
        <div className="flex items-center justify-between gap-2">
          {category.transactionCount !== undefined ? (
            <p className="text-ink-500 text-xs">
              {t.household.categories.matchCountSuffix(category.transactionCount)}
            </p>
          ) : (
            <span />
          )}
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
